import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

type DemoUser = {
  label: string;
  email: string;
  password: string;
};

const demoUsers: DemoUser[] = (() => {
  const raw = process.env.REACT_APP_DEMO_USERS_JSON;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((u) => u && typeof u.email === 'string' && typeof u.password === 'string')
      .map((u) => ({
        label: typeof u.label === 'string' && u.label.trim() ? u.label.trim() : u.email,
        email: u.email,
        password: u.password,
      }));
  } catch {
    return [];
  }
})();

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedDemoUser, setSelectedDemoUser] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md">
        <div className="mb-2 flex items-center justify-center gap-3">
          <img
            src="/asset/tngrm.jpeg"
            alt="TNGRM logo"
            className="h-12 w-auto rounded object-contain"
          />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Test Data Hub
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-center mb-6">Sign in to your account</p>
        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 space-y-4"
        >
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="demo-user" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Demo User
            </label>
            <select
              id="demo-user"
              value={selectedDemoUser}
              onChange={(e) => {
                const selected = e.target.value;
                setSelectedDemoUser(selected);
                const demo = demoUsers.find((u) => u.email === selected);
                if (demo) {
                  setEmail(demo.email);
                  setPassword(demo.password);
                  setError('');
                }
              }}
              disabled={demoUsers.length === 0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="">Select a demo user...</option>
              {demoUsers.map((u) => (
                <option key={u.email} value={u.email}>
                  {u.label}
                </option>
              ))}
            </select>
            {demoUsers.length === 0 && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                No demo users configured. Add REACT_APP_DEMO_USERS_JSON in frontend .env and restart frontend.
              </p>
            )}
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-md transition"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            Don't have an account?{' '}
            <Link to="/register" className="text-blue-600 dark:text-blue-400 hover:underline">
              Register
            </Link>
          </p>
        </form>
        <p className="text-center mt-4">
          <Link to="/" className="text-sm text-gray-500 dark:text-gray-400 hover:underline">
            ← Back to app
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
